/* global debounce */
if (!customElements.get("media-gallery")) {
  customElements.define(
    "media-gallery",
    class MediaGallery extends HTMLElement {
      constructor() {
        super();
        this.elements = {
          liveRegion: this.querySelector('[id^="GalleryStatus"]'),
          viewer: this.querySelector('[id^="GalleryViewer"]'),
          thumbnails: this.querySelector('[id^="GalleryThumbnails"]'),
        };
        this.mql = window.matchMedia("(min-width: 750px)");

        // Clicking a non-active slide sets it active; only the active slide opens the lightbox
        this.elements.viewer
          .querySelectorAll(".product__media-toggle")
          .forEach((btn) => {
            btn.addEventListener(
              "click",
              (e) => {
                const slide = btn.closest(".slider__slide");
                if (slide && !slide.classList.contains("is-active")) {
                  e.stopImmediatePropagation();
                  this.setActiveMedia(slide.dataset.mediaId, false);
                }
              },
              true,
            );
          });

        if (!this.elements.thumbnails) {
          return;
        }

        this.elements.viewer.addEventListener(
          "slideChanged",
          debounce(this.onSlideChanged.bind(this), 500),
        );
        this.elements.thumbnails
          .querySelectorAll("[data-target]")
          .forEach((mediaToSwitch) => {
            mediaToSwitch
              .querySelector("button")
              .addEventListener("click", () => {
                mediaToSwitch.classList.toggle("thumb-active");
                this.setActiveMedia(mediaToSwitch.dataset.target, false);
                this.syncVariantFromThumbnail(mediaToSwitch);
              });
          });
        if (
          this.dataset.desktopLayout.includes("thumbnail") &&
          this.mql.matches
        ) {
          this.removeListSemantic();
        }
      }

      onSlideChanged(event) {
        const thumbnail = this.elements.thumbnails.querySelector(
          `[data-target="${event.detail.currentElement.dataset.mediaId}"]`,
        );
        this.setActiveThumbnail(thumbnail);
      }

      setActiveMedia(mediaId, prepend) {
        const activeMedia =
          this.elements.viewer.querySelector(`[data-media-id="${mediaId}"]`) ||
          this.elements.viewer.querySelector("[data-media-id]");
        if (!activeMedia) {
          return;
        }
        this.elements.viewer
          .querySelectorAll("[data-media-id]")
          .forEach((element) => {
            element.classList.remove("is-active");
          });
        activeMedia?.classList?.add("is-active");

        if (prepend) {
          activeMedia.parentElement.firstChild !== activeMedia &&
            activeMedia.parentElement.prepend(activeMedia);

          if (this.elements.thumbnails) {
            const activeThumbnail = this.elements.thumbnails.querySelector(
              `[data-target="${mediaId}"]`,
            );
            if (activeThumbnail) {
              activeThumbnail.parentElement.firstChild !== activeThumbnail &&
                activeThumbnail.parentElement.prepend(activeThumbnail);
            }
          }

          if (this.elements.viewer.slider) {
            this.elements.viewer.resetPages();
          }
        }

        this.preventStickyHeader();
        window.setTimeout(() => {
          if (!this.mql.matches || this.elements.thumbnails) {
            activeMedia.parentElement.scrollTo({
              left: activeMedia.offsetLeft,
            });
          }
          const activeMediaRect = activeMedia.getBoundingClientRect();
          // Don't scroll if the image is already in view
          if (activeMediaRect.top > -0.5) {
            return;
          }
          const top = activeMediaRect.top + window.scrollY;
          window.scrollTo({ top: top, behavior: "smooth" });
        });
        this.playActiveMedia(activeMedia);

        if (!this.elements.thumbnails) {
          return;
        }
        const activeThumbnail = this.elements.thumbnails.querySelector(
          `[data-target="${mediaId}"]`,
        );
        this.setActiveThumbnail(activeThumbnail);
        if (activeThumbnail) {
          this.announceLiveRegion(
            activeMedia,
            activeThumbnail.dataset.mediaPosition,
          );
        }
      }

      syncVariantFromThumbnail(thumbnailEl) {
        const variantSelects =
          this.closest("product-info")?.querySelector("variant-selects");
        if (!variantSelects) {
          return;
        }

        let lastChanged = this.syncVariantInputs(
          variantSelects,
          thumbnailEl.dataset.target,
        );
        if (!lastChanged) {
          lastChanged = this.syncFrameInput(
            variantSelects,
            thumbnailEl.dataset.frame,
          );
        }
        if (lastChanged) {
          lastChanged.dispatchEvent(new Event("change", { bubbles: true }));
        }
      }

      syncVariantInputs(variantSelects, target) {
        if (!target) {
          return null;
        }
        const mediaIdMatch = target.match(/-(\d+)-\d+$/);
        if (!mediaIdMatch) {
          return null;
        }
        const mediaId = parseInt(mediaIdMatch[1]);
        const variantsScript = this.querySelector(
          '[id^="ProductVariantsData-"]',
        );
        if (!variantsScript) {
          return null;
        }
        let variants;
        try {
          variants = JSON.parse(variantsScript.textContent);
        } catch {
          variants = null;
        }
        if (!variants) {
          return null;
        }
        const variant = variants.find((v) => v.featured_media_id === mediaId);
        if (!variant) {
          return null;
        }

        let lastChanged = null;
        variantSelects
          .querySelectorAll("fieldset")
          .forEach((fieldset, index) => {
            const optionValue = variant.options[index];
            if (optionValue === undefined) {
              return;
            }
            const input = Array.from(
              fieldset.querySelectorAll('input[type="radio"]'),
            ).find((i) => i.value === optionValue);
            if (input && !input.checked) {
              input.checked = true;
              lastChanged = input;
            }
          });
        variantSelects.querySelectorAll("select").forEach((select, index) => {
          const optionValue = variant.options[index];
          if (optionValue === undefined || select.value === optionValue) {
            return;
          }
          select.value = optionValue;
          lastChanged = select;
        });
        return lastChanged;
      }

      syncFrameInput(variantSelects, frameValue) {
        if (frameValue === undefined) {
          return null;
        }
        const frameEl = variantSelects.querySelector(
          "[data-frame-option-index]",
        );
        if (!frameEl) {
          return null;
        }
        const select = frameEl.querySelector("select");
        if (select) {
          const opt = Array.from(select.options).find(
            (o) =>
              o.value.toLowerCase().replace("no frame", "none") === frameValue,
          );
          if (opt && select.value !== opt.value) {
            select.value = opt.value;
            return select;
          }
          return null;
        }
        const input = Array.from(
          frameEl.querySelectorAll('input[type="radio"]'),
        ).find(
          (i) =>
            i.value.toLowerCase().replace("no frame", "none") === frameValue,
        );
        if (input && !input.checked) {
          input.checked = true;
          return input;
        }
        return null;
      }

      setActiveThumbnail(thumbnail) {
        if (!this.elements.thumbnails || !thumbnail) {
          return;
        }

        this.elements.thumbnails
          .querySelectorAll("button")
          .forEach((element) => element.removeAttribute("aria-current"));
        thumbnail.querySelector("button").setAttribute("aria-current", true);
        if (this.elements.thumbnails.isSlideVisible(thumbnail, 10)) {
          return;
        }

        this.elements.thumbnails.slider.scrollTo({
          left: thumbnail.offsetLeft,
        });
      }

      announceLiveRegion(activeItem, position) {
        const image = activeItem.querySelector(
          ".product__modal-opener--image img",
        );
        if (!image) {
          return;
        }
        image.onload = () => {
          this.elements.liveRegion.setAttribute("aria-hidden", false);
          this.elements.liveRegion.innerHTML =
            window.accessibilityStrings.imageAvailable.replace(
              "[index]",
              position,
            );
          setTimeout(() => {
            this.elements.liveRegion.setAttribute("aria-hidden", true);
          }, 2000);
        };
        image.src = image.getAttribute("src");
      }

      playActiveMedia(activeItem) {
        window.pauseAllMedia();
        const deferredMedia = activeItem.querySelector(".deferred-media");
        if (deferredMedia) {
          deferredMedia.loadContent(false);
        }
      }

      preventStickyHeader() {
        this.stickyHeader =
          this.stickyHeader || document.querySelector("sticky-header");
        if (!this.stickyHeader) {
          return;
        }
        this.stickyHeader.dispatchEvent(new Event("preventHeaderReveal"));
      }

      removeListSemantic() {
        if (!this.elements.viewer.slider) {
          return;
        }
        this.elements.viewer.slider.setAttribute("role", "presentation");
        this.elements.viewer.sliderItems.forEach((slide) =>
          slide.setAttribute("role", "presentation"),
        );
      }
    },
  );
}
